import React, { useCallback, useEffect, useState } from "react";
import { Database, Loader2, RefreshCcw, Eye, ArrowRightLeft, Undo2, AlertTriangle, Lock } from "lucide-react";
import { Tile, Badge } from "../../Primitives";
import ReasonModal from "./ReasonModal";
import { relTime } from "../../apikeys/shared";
import { getActualStatus, checkActual, previewActual, switchSource } from "../../../lib/classificationApi";
import { formatPeso, formatNumber, cn } from "../../../lib/utils";

const CELLS = ["AX", "AY", "AZ", "BX", "BY", "BZ", "CX", "CY", "CZ", "ZI"];
const btn = "inline-flex items-center gap-1.5 px-3 py-1.5 text-[11.5px] rounded-sm disabled:opacity-40 transition-[opacity,border-color,color]";

function ModeBadge({ st }) {
  if (st.mode !== "actual") return <Badge status="info" testid="actual-mode">Sample data (synthetic)</Badge>;
  return st.practice ? <Badge status="warning" testid="actual-mode">Practice files · not real data</Badge>
                     : <Badge status="success" testid="actual-mode">Actual 1Rotary data</Badge>;
}

function Readiness({ st }) {
  const n = st.months_available || 0;
  const pct = Math.min(1, n / st.min_months) * 100;
  return (
    <div data-testid="actual-readiness" className="space-y-2">
      <div className="flex items-baseline justify-between text-[11px]">
        <span className="uppercase tracking-widest text-[10px] font-semibold text-muted">Sales history readable</span>
        <span data-testid="actual-months" className="font-mono-tabular font-semibold">{n} of {st.min_months} months needed{n >= st.min_months ? ` · ${st.months_used} will be used (max ${st.max_months})` : ""}</span>
      </div>
      <div className="h-1.5 bg-surface-2 rounded-sm overflow-hidden"><div className={cn("h-full", n >= st.min_months ? "bg-success" : "bg-warning")} style={{ width: `${pct}%` }} /></div>
      {st.first_date && (
        <div className="text-[11px] text-muted font-mono-tabular flex flex-wrap gap-x-4 gap-y-0.5">
          <span>{st.first_date} → {st.last_date}</span><span>{formatNumber(st.items_sold)} items sold</span>
          <span>{formatNumber(st.items_with_cost)} with a unit cost</span><span>{formatNumber(st.branches)} branches</span>
        </div>
      )}
      {(st.reasons || []).map(r => <div key={r} className="text-[11px] text-warning flex items-start gap-1.5"><AlertTriangle size={11} className="mt-0.5 shrink-0" />{r}</div>)}
    </div>
  );
}

function CheckLine({ st }) {
  if (st.running) {
    const p = st.progress || {};
    return <div data-testid="actual-running" className="text-[11px] text-primary inline-flex items-center gap-1.5"><Loader2 size={11} className="animate-spin" />Reading S3 files… {p.total ? `${formatNumber(p.done)} / ${formatNumber(p.total)} day files` : ""}</div>;
  }
  if (!st.last_attempt_at) return <div data-testid="actual-last-check" className="text-[11px] text-muted">S3 has not been checked yet.</div>;
  return (
    <div data-testid="actual-last-check" className="text-[11px] text-muted">
      Last checked {relTime(st.last_attempt_at)} · {st.ok ? <span className="text-success">read successfully</span> : <span className="text-critical">failed</span>}
      {st.ok && st.source && <span> · {st.source}</span>}
      {st.error && <div data-testid="actual-error" className="mt-1 text-critical">{st.error}</div>}
    </div>
  );
}

function Compare({ pv, current }) {
  const tot = (side) => ({ count: pv[side].skus, value: pv[side].total_value });
  const row = (label, l, a, key) => (
    <tr key={key} className="border-b border-border/60">
      <td className="px-3 py-1.5 font-mono-tabular font-semibold">{label}</td>
      <td className="px-3 py-1.5 text-right font-mono-tabular">{formatNumber(l.count)}</td>
      <td className="px-3 py-1.5 text-right font-mono-tabular text-muted">{formatPeso(l.value, { compact: true })}</td>
      <td className="px-3 py-1.5 text-right font-mono-tabular">{formatNumber(a.count)}</td>
      <td className="px-3 py-1.5 text-right font-mono-tabular text-muted">{formatPeso(a.value, { compact: true })}</td>
    </tr>
  );
  return (
    <div data-testid="actual-preview" className="border border-border rounded-sm">
      <div className="px-3 py-2 border-b border-border flex flex-wrap items-center gap-2 text-[11px]">
        <span className="uppercase tracking-widest text-[10px] font-semibold text-muted">Side-by-side preview</span>
        <span className="text-muted">built {relTime(pv.built_at)} · {pv.months} months ({pv.window.start} → {pv.window.end})</span>
        {!current && <Badge status="warning" testid="actual-preview-stale">Out of date — rebuild before switching</Badge>}
      </div>
      {pv.practice && <div data-testid="actual-preview-practice" className="px-3 py-1.5 text-[11px] text-warning border-b border-border bg-warning/5">{pv.source}</div>}
      <div className="overflow-x-auto">
        <table className="w-full text-[11.5px]">
          <thead><tr className="text-[9.5px] uppercase tracking-widest text-subtle border-b border-border">
            <th className="text-left px-3 py-1.5">Cell</th><th className="text-right px-3 py-1.5" colSpan={2}>Live when built ({pv.live.mode})</th>
            <th className="text-right px-3 py-1.5" colSpan={2}>{pv.practice ? "Practice data" : "Actual data"}</th>
          </tr></thead>
          <tbody>
            {CELLS.map(c => row(c === "ZI" ? "Z-Int" : c, pv.live.cells[c], pv.actual.cells[c], c))}
            {row("Total", tot("live"), tot("actual"), "total")}
          </tbody>
        </table>
      </div>
      <div className="px-3 py-2 text-[10.5px] text-muted space-y-0.5 border-t border-border">
        <div>{formatNumber(pv.excluded_no_cost)} items left out — no unit cost on any supplier receipt yet.</div>
        <div>{formatNumber(pv.lead_time_defaulted)} items use the default 30-day lead time — their supplier has no lead time recorded.</div>
        <div>Top items by annual value: {pv.actual.top.slice(0, 5).map(t => `${t.name} (${t.cell})`).join(" · ")}</div>
      </div>
    </div>
  );
}

// Actual Data Switch: readiness from S3 (≥ 12 months), side-by-side preview, IT Head confirmation (reversible).
export default function ActualDataPanel({ user, onChanged }) {
  const isIT = user?.role === "it_head";
  const [st, setSt] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const load = useCallback(() => getActualStatus().then(x => { setSt(x); setError(null); return x; }).catch(e => setError(e.message)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!st?.running || busy) return undefined;
    const t = setInterval(load, 2000);
    return () => clearInterval(t);
  }, [st?.running, busy, load]);

  async function check() {
    setBusy("check"); setError(null);
    const prev = st?.last_attempt_at;
    try {
      await checkActual(user);
      for (let i = 0; i < 600; i++) {
        await new Promise(r => setTimeout(r, 1500));
        const x = await load();
        if (x && !x.running && x.last_attempt_at !== prev) break;
      }
    } catch (e) { setError(e.message); }
    setBusy(null);
  }
  async function preview() {
    setBusy("preview"); setError(null);
    try { await previewActual(user); await load(); } catch (e) { setError(e.message); }
    setBusy(null);
  }

  const toActual = confirm === "actual";
  return (
    <Tile testid="actual-data" title={<span className="text-[11px] font-semibold tracking-widest uppercase text-muted inline-flex items-center gap-2"><Database size={12} className="text-primary" /> Data source · Actual data switch</span>}
          action={st && <ModeBadge st={st} />}>
      {!st && !error && <div className="text-[12px] text-muted"><Loader2 size={13} className="inline animate-spin mr-2" />Loading data source…</div>}
      {error && <div data-testid="actual-panel-error" className="mb-3 text-[11.5px] text-critical border border-critical/30 bg-critical/5 rounded-sm p-2.5">{error}</div>}
      {st && (
        <div className="space-y-3.5">
          <p className="text-[11.5px] text-muted leading-snug max-w-3xl">The matrix classifies the sample until 1Rotary's own sales history is readable from S3 with at least {st.min_months} complete months.
            The IT Head reviews a side-by-side preview and confirms the switch; it can be switched back with a written reason.</p>
          {st.practice_reader && <div data-testid="actual-practice-note" className="text-[11px] text-warning border border-warning/30 bg-warning/5 rounded-sm px-3 py-1.5">Practice files shaped like the 1Rotary S3 layout are being read for testing — they are not real data.</div>}
          <Readiness st={st} />
          <CheckLine st={st} />
          {st.preview && <Compare pv={st.preview} current={st.preview_current} />}
          {isIT ? (
            <div className="flex flex-wrap items-center gap-2">
              <button data-testid="actual-check" onClick={check} disabled={!!busy || st.running} className={cn(btn, "border border-border hover:border-primary hover:text-primary")}>
                {busy === "check" || st.running ? <Loader2 size={12} className="animate-spin" /> : <RefreshCcw size={12} />} Check S3 now
              </button>
              <button data-testid="actual-build-preview" onClick={preview} disabled={!!busy || !st.ready || st.running} className={cn(btn, "border border-border hover:border-primary hover:text-primary")}>
                {busy === "preview" ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />} {busy === "preview" ? "Classifying actual data…" : "Build side-by-side preview"}
              </button>
              {st.mode !== "actual" ? (
                <button data-testid="actual-switch" onClick={() => setConfirm("actual")} disabled={!!busy || !st.ready || !st.preview_current}
                        className={cn(btn, "font-semibold bg-primary text-primary-fg hover:opacity-90")}><ArrowRightLeft size={12} /> Switch to actual data</button>
              ) : (
                <button data-testid="actual-switch-back" onClick={() => setConfirm("sample")} disabled={!!busy}
                        className={cn(btn, "border border-warning/50 text-warning hover:bg-warning/10")}><Undo2 size={12} /> Switch back to sample</button>
              )}
            </div>
          ) : (
            <div data-testid="actual-readonly" className="text-[11px] text-muted inline-flex items-center gap-1.5"><Lock size={11} /> Only the IT Head can check S3, build the preview and switch the data source.</div>
          )}
        </div>
      )}
      {confirm && (
        <ReasonModal testid="actual-confirm" title={toActual ? "Switch the matrix to actual data" : "Switch back to sample data"}
                     subtitle="Recorded in the change history · re-runs the pipeline" confirmLabel={toActual ? "Switch to actual data" : "Switch back to sample"}
                     onClose={() => setConfirm(null)}
                     impact={toActual ? <>
                       <div>Every ABC × XYZ class, the drill-down, branch views and the engines' features will use {st.practice ? <strong className="text-warning">practice files (not real data)</strong> : "1Rotary's own sales, unit costs and supplier lead times"} — {st.preview.actual.skus.toLocaleString()} items, {st.preview.months} months.</div>
                       <div className="text-muted">Tomorrow's class-change summary will not compare across the switch.</div>
                     </> : <div>The matrix goes back to the synthetic sample catalogue. Actual data stays ingested and can be switched on again.</div>}
                     onConfirm={async (reason) => {
                       const res = await switchSource(confirm, reason, user);
                       await load();
                       onChanged(res, !toActual ? "Switched back to sample data" : st.practice ? "Switched to practice files (not real data)" : "Switched to actual data");
                     }} />
      )}
    </Tile>
  );
}
